import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CheckCircle, Loader, AlertCircle, Link as LinkIcon, Copy, ExternalLink } from 'lucide-react';
import { useWordPress } from '../context/WordPressContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1';

export default function Connect() {
  const navigate = useNavigate();
  const location = useLocation();
  const { wordpressUrl, isInWordPress } = useWordPress();

  const [state, setState] = useState('generating'); // generating, ready, connecting, connected, error
  const [magicLink, setMagicLink] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const storeId = location.state?.storeId;
  const wpUrl = wordpressUrl || location.state?.wordpressUrl;

  useEffect(() => {
    if (!storeId || !wpUrl) {
      setError('Missing store information. Please try signing up again.');
      setState('error');
      return;
    }

    generateMagicLink();
  }, [storeId, wpUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  async function generateMagicLink() {
    try {
      setState('generating');

      console.log('Generating magic link for store:', storeId);
      console.log('WordPress URL:', wpUrl);

      const response = await axios.post(`${API_URL}/stores/create-magic-link`, {
        store_id: storeId,
        wordpress_url: wpUrl
      });

      const { magic_link } = response.data;
      setMagicLink(magic_link);
      setState('ready');

      // If in WordPress iframe, automatically connect after a brief moment
      if (isInWordPress) {
        setTimeout(() => {
          autoConnect(magic_link);
        }, 1000);
      }

    } catch (err) {
      console.error('Error generating magic link:', err);
      setError(err.response?.data?.message || 'Failed to generate connection link');
      setState('error');
    }
  }

  function autoConnect(link) {
    setState('connecting');

    // Since we're in an iframe, redirect the parent window
    // The parent (WordPress) will handle the ?vfr_setup=xxx parameter
    if (window.top && window.top !== window.self) {
      window.top.location.href = link;
    } else {
      // Fallback if not in iframe
      window.location.href = link;
    }
  }

  function copyToClipboard() {
    navigator.clipboard.writeText(magicLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function handleManualConnect() {
    autoConnect(magicLink);
  }

  // Render different states
  if (state === 'generating') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-brand-50 to-white flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-card p-8 max-w-md w-full text-center">
          <Loader className="w-16 h-16 text-brand-600 animate-spin mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Preparing Connection</h2>
          <p className="text-surface-600">
            Generating secure connection link...
          </p>
        </div>
      </div>
    );
  }

  if (state === 'connecting') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-brand-50 to-white flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-card p-8 max-w-md w-full text-center">
          <div className="relative mb-6">
            <Loader className="w-16 h-16 text-brand-600 animate-spin mx-auto" />
            <CheckCircle className="w-8 h-8 text-success-500 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Connecting Your Site</h2>
          <p className="text-surface-600 mb-4">
            Please wait while we connect your WordPress site...
          </p>
          <p className="text-sm text-surface-500">
            This will only take a moment
          </p>
        </div>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-brand-50 to-white flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-card p-8 max-w-md w-full">
          <div className="flex items-center justify-center w-16 h-16 bg-danger-100 rounded-full mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-danger-600" />
          </div>
          <h2 className="text-2xl font-bold text-center mb-2">Connection Error</h2>
          <p className="text-surface-600 text-center mb-6">
            {error}
          </p>
          <div className="space-y-3">
            <button
              onClick={() => generateMagicLink()}
              className="w-full bg-brand-600 text-white py-3 rounded-xl hover:bg-brand-700 font-medium"
            >
              Try Again
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full bg-surface-200 text-surface-700 py-3 rounded-xl hover:bg-surface-300 font-medium"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // state === 'ready'
  return (
    <div className="min-h-screen bg-gradient-to-b from-brand-50 to-white flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-panel p-8 max-w-2xl w-full">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-brand-100 rounded-full mb-4">
            <LinkIcon className="w-8 h-8 text-brand-600" />
          </div>
          <h2 className="text-3xl font-bold mb-2">Connect Your WordPress Site</h2>
          <p className="text-surface-600">
            {isInWordPress
              ? 'Connection will happen automatically in a moment...'
              : 'Click the button below to complete the connection'
            }
          </p>
        </div>

        {/* Auto-connecting message for iframe */}
        {isInWordPress && (
          <div className="bg-brand-50 border-l-4 border-brand-500 p-4 mb-6">
            <div className="flex items-start">
              <Loader className="w-5 h-5 text-brand-600 animate-spin mr-3 mt-0.5" />
              <div>
                <p className="text-sm text-brand-800">
                  <strong>Auto-connecting your WordPress site...</strong>
                </p>
                <p className="text-xs text-brand-700 mt-1">
                  You'll be redirected automatically. If this doesn't happen, click the button below.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Connection Options */}
        <div className="space-y-4">
          <button
            onClick={handleManualConnect}
            className="w-full bg-brand-600 text-white py-4 rounded-xl hover:bg-brand-700 font-medium text-lg flex items-center justify-center space-x-2"
          >
            <ExternalLink className="w-5 h-5" />
            <span>Complete Connection</span>
          </button>

          {/* Manual link option */}
          <details className="bg-surface-50 rounded-xl p-4">
            <summary className="cursor-pointer font-medium text-surface-700 hover:text-surface-900">
              Alternative: Copy Connection Link
            </summary>
            <div className="mt-4 space-y-3">
              <p className="text-sm text-surface-600">
                Copy this link and open it in your WordPress admin:
              </p>
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={magicLink}
                  readOnly
                  className="flex-1 px-3 py-2 border border-surface-300 rounded-xl bg-white text-sm font-mono"
                  onClick={(e) => e.target.select()}
                />
                <button
                  onClick={copyToClipboard}
                  className="px-4 py-2 bg-surface-200 hover:bg-surface-300 rounded-xl flex items-center space-x-2"
                >
                  <Copy className="w-4 h-4" />
                  <span className="text-sm">{copied ? 'Copied!' : 'Copy'}</span>
                </button>
              </div>
              <p className="text-xs text-surface-500">
                <strong>Note:</strong> This link expires in 15 minutes for security.
              </p>
            </div>
          </details>
        </div>

        {/* Troubleshooting */}
        <div className="mt-8 p-4 bg-surface-50 rounded-xl">
          <h3 className="font-semibold text-surface-900 mb-2">Having trouble?</h3>
          <ul className="text-sm text-surface-600 space-y-1">
            <li>• Make sure you're logged into WordPress as an administrator</li>
            <li>• If the link expired, click "Try Again" to generate a new one</li>
            <li>• For support, contact us at support@virtualfittingroom.com</li>
          </ul>
        </div>

        {/* Back button */}
        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/dashboard')}
            className="text-surface-600 hover:text-surface-900 text-sm"
          >
            Skip for now (you can connect later)
          </button>
        </div>
      </div>
    </div>
  );
}
